import { validationMessages } from "../common/validationMessages";
import * as Yup from "yup";

export const tableSize = 50;

export interface DriversApiResponse {
  items: DriverItem[];
  nextCursor: number | null;
}

export interface DriverResponse {
  id: number;
  driverId: string;
  firstName: string;
  lastName: string;
  phone: string;
  domicileTerminalNumber: string;
  status: string;
  statusChangedAt: string;
  statusChangedBy: string;
  lastTelematicsSyncedAt: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  address: DriverAddress;
  commercialDriverLicense: CommercialDriverLicense;
  payRates: PayRate[];
}

export interface DriverAddress {
  id: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface CommercialDriverLicense {
  id: string;
  licenseNumber: string;
  licenseClass: string;
  issueState: string;
  issuedDate: string;
  expirationDate: string;
  medicalCardExpiration: string;
  endorsements: CDLCode[];
  restrictions: CDLCode[];
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface CDLCode {
  id: string;
  code: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  commercialDriverLicenseId: string;
}
export interface PayRate {
  id: string;
  payType: string; // could be union: "HOURLY" | "MILEAGE" | etc.
  amount: number;
  adjustment: number;
  editable: boolean;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface DriverItem {
  id: number;
  driverId: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  domicileTerminalNumber: string | null;
  status: string;
  statusChangedAt: string | null;
  statusChangedBy: string | null;
  lastTelematicsSyncedAt: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
  address: Address | null;
  commercialDriverLicense: CommercialDriverLicense | null;
  payRates: PayRate[];
}

export interface Address {
  [key: string]: any;
}

export interface CommercialDriverLicense {
  id: string;
  licenseNumber: string;
  licenseClass: string;
  issueState: string;
  issuedDate: string;
  expirationDate: string;
  medicalCardExpiration: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  driverInternalId: number;
  endorsements: CDLCode[];
  restrictions: CDLCode[];
}

export interface PayRate {
  id: string;
  payType: string;
  amount: number;
  adjustment: number;
  editable: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  driverInternalId: number;
}

export const cdlClassOptions = [
  { label: "A", value: "A" },
  { label: "B", value: "B" },
  { label: "C", value: "C" },
];

export const driverStatuses = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

export interface DriverDetails {
  driverId: string;
  firstName: string;
  lastName: string;
  commercialDriverLicense: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  phone: string;
  domicileTerminalNumber: string;
  status: string;
  licenseClass: string;
  expirationDate: string;
}

export const driverValidation = () => Yup.object({
  firstName: Yup.string().required(validationMessages.required).max(64, "The value must be no more than 64 characters"),
  lastName: Yup.string().required(validationMessages.required).max(64, "The value must be no more than 64 characters"),
  commercialDriverLicense: Yup.string().required(validationMessages.required),
  licenseClass: Yup.string().required(validationMessages.required),
  street: Yup.string().matches(/\S+/, validationMessages.notValid).max(256, "The value must be no more than 256 characters").required(validationMessages.required),
  city: Yup.string().required(validationMessages.required).max(64, "The value must be no more than 64 characters"),
  state: Yup.string().required(validationMessages.required),
  zipcode: Yup.string().matches(/^\d*$/, validationMessages.notValid).min(5, "The value must be no less than 5 digits").max(5, "The value must be no more than 5 digits").required(validationMessages.required),
  phone: Yup.string()
    .nullable()
    .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, validationMessages.notValid)
    .test("emailOrPhone", "Either email or phone is required", function (value) {
      const { email, phone } = this.parent;
      const isEmailProvided = email && email.trim() !== "";
      const isPhoneProvided = phone && phone.trim() !== "";
      return isEmailProvided || isPhoneProvided;
    }), domicileTerminalNumber: Yup.string().required(validationMessages.required),
  status: Yup.string().required(validationMessages.required),
  expirationDate: Yup.string().required(validationMessages.required).nullable(),
})

export interface DriverRequest {
  driverId: string;
  firstName: string;
  lastName: string;
  phone: string;
  domicileTerminalNumber: string;
  status: string; // e.g. "active" | "inactive"
  address: DriverAddressRequest;
  commercialDriverLicense: CommercialDriverLicenseRequest;
  payRates: PayRateRequest[];
}

/* ---------------- Address ---------------- */

export interface DriverAddressRequest {
  street: string;
  city: string;
  state: string;
  zipcode: string;
}

/* ---------------- Commercial Driver License ---------------- */

export interface CommercialDriverLicenseRequest {
  licenseNumber: string;
  licenseClass: string; // "A" | "B" | "C"
  issueState: string;
  issuedDate: string;
  expirationDate: string;
  medicalCardExpiration: string;
  endorsements: CDLCodeRequest[];
  restrictions: CDLCodeRequest[];
}

export interface CDLCodeRequest {
  code: string;
}

/* ---------------- Pay Rate ---------------- */

export interface PayRateRequest {
  payType: string; // "HOURLY" etc.
  amount: number;
  adjustment: number;
  editable: boolean;
}

export const endorsementMapping = {
  H: "HazMat",
  N: "Tank Vehicle",
  P: "Passenger",
  T: "Double/Triple Trailers",
  X: "Tank + HazMat Combo",
};
